/**
 * Preview as client: the client's own review page (client-portal/review,
 * style B), drawn from the team's month with every answer and comment off.
 * The team's slots, concept versions and briefs are adapted into the shapes
 * the portal serves, so the preview is the page the client opens, not a copy.
 */
import { useCallback, useMemo, useState } from "react";
import { ArrowLeft, Eye } from "lucide-react";
import {
  latestReviewDecision,
  type CalendarPlanSlot,
  type CalendarSlotIdea,
  type ConceptVersion,
  type ConceptVersionsResponse,
  type IdeaComment,
} from "@/types/contentCalendar.type";
import type {
  ClientSlotPhase,
  PortalComment,
  PortalConceptVersion,
  PortalDecision,
  PortalIdea,
  PortalSlot,
} from "@/types/clientPortal.type";
import { liveBody, type ProductionBrief } from "@/types/productionBrief.type";
import { scrubVendorTermsDeep } from "@/lib/vendorTerms";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { CoShellPortalContext } from "@/app/routes/dashboard/CoShellPortalContext";
import ClientReviewPage from "@/app/routes/client-portal/review/ClientReviewPage";
import type { ClientPost } from "@/app/routes/client-portal/clientReviewModel";
import { PREVIEW_PORTAL_SOURCE } from "@/app/routes/client-portal/api/clientPortalApi";
import type { ClientBrief } from "@/app/routes/client-portal/api/useClientBriefs";
import { acceptedIdea } from "../slotStage";
import { useConceptVersions } from "../api/useConceptVersions";
import { clientClaim } from "./ClientMonthView";

/** A team-side comment as the portal serves it: shared only, no visibility flag. */
function portalComments(comments: IdeaComment[] | undefined): PortalComment[] {
  return (comments ?? [])
    .filter((comment) => comment.visibility !== "INTERNAL")
    .map((comment) => {
      const { visibility, ...rest } = comment;
      void visibility;
      return rest as PortalComment;
    });
}

function portalIdea(idea: CalendarSlotIdea): PortalIdea {
  return {
    id: idea.id,
    title: idea.title,
    synopsis: idea.synopsis,
    hook: idea.hooks[0] ?? null,
    reference: idea.reference,
    sources: idea.sources.map((source) => ({ label: source.label, url: source.url ?? undefined })),
    objectiveClaim: clientClaim(idea.objectiveClaim),
    channels: idea.channels,
    aspectRatio: idea.aspectRatio,
    durationSeconds: idea.durationSeconds,
    updatedAt: idea.updatedAt,
    comments: portalComments(idea.comments),
  };
}

/** The sent versions as the client reads them; an unreadable snapshot falls back to the idea. */
function portalVersions(slot: CalendarPlanSlot, idea: CalendarSlotIdea, versions: ConceptVersion[]): PortalConceptVersion[] {
  return versions.map((version) => {
    const snapshot = version.snapshot;
    return {
      id: version.id,
      slotId: slot.id,
      roundId: version.roundId,
      ideaId: version.ideaId,
      version: version.version,
      sharedAt: version.sharedAt,
      isLatest: version.isLatest,
      readOnly: version.readOnly,
      snapshot: {
        id: snapshot?.id ?? idea.id,
        title: snapshot?.title || idea.title || slot.title,
        synopsis: snapshot?.synopsis ?? idea.synopsis,
        hooks: snapshot?.hooks ?? idea.hooks,
        objectiveClaim: clientClaim(snapshot?.objectiveClaim ?? idea.objectiveClaim),
        channels: snapshot?.channels ?? idea.channels,
        aspectRatio: snapshot?.aspectRatio ?? idea.aspectRatio,
        durationSeconds: snapshot?.durationSeconds ?? idea.durationSeconds,
        reference: snapshot?.reference ?? idea.reference,
        sources: (snapshot?.sources ?? idea.sources).map((source) => ({ label: source.label, url: source.url ?? undefined })),
      },
      comments: portalComments(version.comments),
      decision: version.decision,
    };
  });
}

/** A post the client has not been sent any version of yet: the chosen idea as version 1. */
function draftVersion(slot: CalendarPlanSlot, idea: CalendarSlotIdea, decision: PortalDecision | null): PortalConceptVersion {
  return {
    id: idea.id,
    slotId: slot.id,
    roundId: "",
    ideaId: idea.id,
    version: 1,
    sharedAt: idea.decidedAt ?? idea.updatedAt,
    isLatest: true,
    readOnly: false,
    snapshot: {
      id: idea.id,
      title: idea.title || slot.title,
      synopsis: idea.synopsis,
      hooks: idea.hooks,
      objectiveClaim: clientClaim(idea.objectiveClaim),
      channels: idea.channels,
      aspectRatio: idea.aspectRatio,
      durationSeconds: idea.durationSeconds,
      reference: idea.reference,
      sources: idea.sources.map((source) => ({ label: source.label, url: source.url ?? undefined })),
    },
    comments: portalComments(idea.comments),
    decision: decision ? { decision: decision.decision, reason: decision.reason, decidedAt: decision.decidedAt, clientName: decision.clientName } : null,
  };
}

/** The team's slot as the portal would serve it, or null while it has no chosen idea. */
function previewSlot(slot: CalendarPlanSlot, history: ConceptVersionsResponse | undefined): PortalSlot | null {
  const idea = acceptedIdea(slot);
  if (!idea) return null;
  const latest = latestReviewDecision(slot);
  // A decline on a version that has since been re-sent is history, not the post's answer.
  const decision: PortalDecision | null =
    latest && (slot.status === "CLIENT_APPROVED" ? latest.decision === "APPROVED" : latest.decision === "DECLINED" && slot.status !== "IN_CLIENT_REVIEW")
      ? {
          decision: latest.decision,
          reason: latest.reason,
          decidedAt: latest.decidedAt,
          clientName: latest.clientName,
          carriedOver: latest.carriedOver,
          mine: false,
        }
      : null;
  const phase: ClientSlotPhase =
    slot.status === "IN_CLIENT_REVIEW"
      ? "awaiting_you"
      : slot.status === "MISSED"
        ? "missed"
        : decision?.decision === "DECLINED"
          ? "in_progress"
          : "decided";
  const sent = history?.versions.length ? portalVersions(slot, idea, history.versions) : [draftVersion(slot, idea, decision)];
  const active =
    (history?.latestSharedVersionId && sent.find((version) => version.id === history.latestSharedVersionId)?.id) ??
    [...sent].sort((a, b) => b.version - a.version)[0]?.id ??
    null;
  return {
    id: slot.id,
    scheduledAt: slot.scheduledAt,
    title: slot.title,
    mediaType: slot.mediaType,
    orientation: slot.orientation,
    aspectRatio: slot.aspectRatio,
    videoLength: slot.videoLength,
    pillarName: slot.pillarName,
    phase,
    actionable: slot.status === "IN_CLIENT_REVIEW",
    idea: portalIdea(idea),
    decision,
    activeConceptVersionId: active,
    conceptVersions: sent,
    approvalSource: slot.status === "CLIENT_APPROVED" ? slot.approvalSource : null,
  };
}

/** The brief the client is reading, never an unsent team draft once a version went out. */
function previewBrief(brief: ProductionBrief, slot: CalendarPlanSlot): ClientBrief | null {
  const visibleVersion = brief.sentVersion ?? brief.version;
  const revision = brief.revisions.find((entry) => entry.version === visibleVersion) ?? brief.revisions[0];
  if (!revision || !liveBody(brief)) return null;
  const decided = brief.status === "ACCEPTED" || brief.status === "CHANGES_REQUESTED";
  return {
    briefId: brief.id,
    slotId: brief.slotId,
    ideaTitle: acceptedIdea(slot)?.title || brief.ideaTitle || slot.title,
    scheduledAt: slot.scheduledAt,
    kind: brief.kind,
    phase: brief.status === "IN_REVIEW" ? "awaiting_you" : brief.status === "DRAFT" && brief.sentVersion !== null ? "revised" : "decided",
    actionable: brief.status === "IN_REVIEW",
    version: visibleVersion,
    body: revision.body,
    decision: decided
      ? {
          decision: brief.status === "ACCEPTED" ? "APPROVED" : "DECLINED",
          reason: null,
          clientName: "Client",
          decidedAt: brief.lastEditedAt ?? brief.updatedAt,
          carriedOver: false,
        }
      : null,
    versions: [{ version: visibleVersion, body: revision.body, reason: null }],
    previousVersion: null,
    comments: [],
    acceptanceSource: brief.status === "ACCEPTED" ? brief.acceptanceSource : null,
  };
}

/** Posts the client has seen or is about to: sent, answered, or signed off and ready to send. */
function clientVisible(slot: CalendarPlanSlot): boolean {
  return (
    slot.status === "SIGNED_OFF" ||
    slot.status === "IN_CLIENT_REVIEW" ||
    slot.status === "CLIENT_APPROVED" ||
    !!latestReviewDecision(slot)
  );
}

export default function ClientPreviewView({
  brandName,
  year,
  month,
  slot,
  slots = [],
  briefs = [],
  onClose,
}: {
  brandName: string;
  year: number;
  month: number;
  /** The post the preview opens on, when it was opened from one. */
  slot: CalendarPlanSlot | null;
  slots?: CalendarPlanSlot[];
  briefs?: ProductionBrief[];
  onClose: () => void;
}) {
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  // The sent versions are read for the post on screen; the others show their chosen idea.
  const [openId, setOpenId] = useState<string | null>(slot?.id ?? null);
  const onSelectPost = useCallback((id: string | null) => setOpenId(id), []);
  const historyQuery = useConceptVersions(openId);
  const history = historyQuery.data;

  const posts: ClientPost[] = useMemo(() => {
    const visible = slots.filter(clientVisible);
    const built = visible.flatMap((candidate) => {
      const portalSlot = previewSlot(candidate, candidate.id === openId ? history : undefined);
      if (!portalSlot) return [];
      const brief = briefs.find((entry) => entry.slotId === candidate.id);
      return [{ slot: portalSlot, brief: brief ? previewBrief(brief, candidate) : null }];
    });
    return scrubVendorTermsDeep(built);
  }, [slots, briefs, openId, history]);

  return (
    <CoShellPortalContext.Provider value={portal}>
      {/* Over the nav island (the drawer layer), under toasts. */}
      <div ref={setPortal} className="fixed inset-0 flex flex-col bg-co-ground text-co-text" style={{ zIndex: "var(--z-index-modal)" }}>
        <header className="co-glass-chrome z-20 flex shrink-0 items-center gap-3 px-4 py-2.5 sm:px-6">
          <Eye className="size-4 shrink-0 text-co-accent" strokeWidth={1.8} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-semibold leading-[19px] text-co-text">Client preview</p>
            <p className="truncate text-[12px] leading-4 text-co-muted">
              What {brandName}’s people see. Answers and comments are off here.
            </p>
          </div>
          <KitButton variant="secondary" iconLeft={ArrowLeft} onClick={onClose} className="shrink-0">
            Exit preview
          </KitButton>
        </header>
        <div className="min-h-0 flex-1">
          <ClientReviewPage
            source={PREVIEW_PORTAL_SOURCE}
            posts={posts}
            brandName={brandName}
            year={year}
            month={month}
            readerName="Client"
            review={null}
            briefReview={null}
            readOnly
            embedded
            fill
            onSelectPost={onSelectPost}
            initial={slot ? { postId: slot.id } : undefined}
          />
        </div>
      </div>
    </CoShellPortalContext.Provider>
  );
}
